import * as geojson from 'geojson'
import * as L from 'leaflet'
import FilterManager from '../tcm_filter_manager'
import { type GeoLocation } from '../tcm_service'
import * as utils from './utils'

import BaseSelectionModeBehaviour from './selection_mode_behaviour'

type LayerType = L.Polygon | L.Polyline

type TerritoryGeoJson =
    | geojson.FeatureCollection<geojson.Geometry, geojson.GeoJsonProperties>
    | geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>
    | geojson.Geometry

type TerritoryLayer = {
    id: string
    name: string
    data: TerritoryGeoJson | null
    defaultVisible: boolean
    __leafletLayer?: L.GeoJSON<TerritoryLayer, geojson.Geometry>
    __selectedLayers: L.Path[]
}

export default class MapSelectionModeBehaviour extends BaseSelectionModeBehaviour {
    private locationMap: Map<LayerType, geojson.Feature> = new Map()

    override onBeforeScopeUpdate(): void {
        this.scope.slider.visible = true
    }

    override applyFilter(mgr: FilterManager): void {
        mgr.localizacaoUfs.clear()

        const ufs = [...this.locationMap.keys()].map((m) =>
            !m.feature || !m.feature.properties ? undefined : m.feature.properties['SIGLA_UF']
        )

        ufs?.length && mgr.localizacaoUfs.add(ufs)
    }

    plotFromFilter(map: L.Map, mgr: FilterManager): void {
        this.removeAllLayers()
        this.locationMap.clear()

        for (const ufs of mgr.localizacaoUfs.values()) {
            for (const uf of ufs) {
                const layer = this.owner.getMapLayerByUF(uf)

                if (!layer || !layer.feature) {
                    continue
                }

                this.locationMap.set(layer, layer.feature)

                layer.setStyle(layerStyle(true))
            }
        }

        this.doDraw(map)
        this.scope.update()
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    override setZoom(_value: number): void {
        // TODO
    }

    override setMode(value: number): void {
        const scope = this.scope
        scope.mode = 'map'
        scope.slider.unit = 'm'
        scope.slider.min = 10
        scope.slider.max = 1000
        scope.slider.step = 10
        scope.slider.value = 50
        this.setZoom(value)
    }

    override clear() {
        super.clear()
        this.locationMap.clear()
        this.owner.filterManager.localizacaoRoutes.clear()
    }

    override hasFilter() {
        return this.locationMap.size > 0
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    override addLocation(location: GeoLocation) {
        // TODO
    }

    clickLocation(feature: geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>) {
        if (!feature) {
            return
        }
        console.log(typeof feature.properties)

        const uf = feature.properties ? feature.properties['SIGLA_UF'] : undefined
        const layer = this.owner.getMapLayerByUF(uf)
        if (!layer) {
            return
        }

        if (this.locationMap.has(layer)) {
            this.locationMap.delete(layer)
            return
        } else {
            if (layer.feature) {
                this.locationMap.set(layer, layer.feature)
                layer.setStyle(layerStyle(true))
            }
        }

        this.scope.update()
    }

    override preparePoints() {
        // TODO
    }

    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    doDraw(map: L.Map) {
        this.removeAllLayers = () => {
            const locationKeys = [...this.locationMap.keys()]
            locationKeys.forEach((l) => l.setStyle(layerStyle(false)))

            this.removeAllLayers = utils.staticNoopOther
        }
        const locationKeys = [...this.locationMap.keys()]
        locationKeys.forEach((l) => l.setStyle(layerStyle(true)))
    }

    mapGeoJsonOptions(): L.GeoJSONOptions<TerritoryLayer, geojson.Geometry> {
        return {
            style: layerStyle(false),
            onEachFeature: (
                feature: geojson.Feature<geojson.Geometry, geojson.GeoJsonProperties>,
                leafletLayer: L.Layer
            ) => {
                leafletLayer.on({
                    mouseover: () => {
                        if (leafletLayer instanceof L.Path) {
                            const selected = this.locationMap.has(leafletLayer as LayerType)

                            leafletLayer.setStyle(territoryHoverStyle(selected))
                            leafletLayer.bringToFront()
                        }
                    },

                    mouseout: () => {
                        if (leafletLayer instanceof L.Path) {
                            const selected = this.locationMap.has(leafletLayer as LayerType)

                            leafletLayer.setStyle(layerStyle(selected))
                        }
                    },

                    click: () => this.clickLocation(feature)
                })
            }
        }
    }
}

function layerStyle(selected: boolean) {
    const base = { color: '#0056b3', weight: 0, fillOpacity: 0.06 }
    if (!selected) {
        return base
    }
    return {
        ...base,
        fillOpacity: Math.min((base.fillOpacity || 0) + 0.2, 0.35)
    }
}

function territoryHoverStyle(selected: boolean) {
    const base = layerStyle(selected)
    return {
        ...base,
        weight: 0.5,
        fillOpacity: Math.min((base.fillOpacity || 0) + 0.1, 0.35),
        color: '#0056c7'
    }
}
